import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { axiosInstance } from '@/App';
import AdminNav from '@/components/AdminNav';
import { 
  FileText, AlertTriangle, Shield, ShieldAlert, Users, GraduationCap, 
  ArrowRight, CheckCircle2, Settings, 
  RefreshCcw, ClipboardList, FileCheck, Presentation, BarChart3, LogOut, MessageSquare, UserPlus, Building2,
  PieChart, Trophy, Target, Brain, Clock, Briefcase, ClipboardCheck, Zap, UserCheck
} from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';

// Check if running in native mobile app (iOS/Android)
const isNativeApp = (() => {
  try {
    if (window.Capacitor?.isNativePlatform?.()) return true;
    const protocol = window.location.protocol;
    if (protocol === 'capacitor:' || protocol === 'ionic:' || protocol === 'file:') return true;
    const hostname = window.location.hostname;
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && !window.location.port) return true;
    return false;
  } catch (e) {
    return false;
  }
})();

const AdminHome = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tileOverrides, setTileOverrides] = useState({});
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    loadStats();
    loadTileOverrides();
    loadPendingCount();
  }, []);

  const loadPendingCount = async () => {
    try {
      const { data } = await axiosInstance.get('/users/pending-approvals/count');
      setPendingCount(data?.count || 0);
    } catch (e) {
      // non-fatal
    }
  };

  const loadTileOverrides = async () => {
    try {
      const { data } = await axiosInstance.get('/admin/tile-config');
      setTileOverrides(data.overrides || {});
    } catch (e) {
      // non-fatal: fall back to defaults
    }
  };

  const loadStats = async () => {
    try {
      // Use admin/analytics endpoint which includes division info
      const analyticsRes = await axiosInstance.get('/admin/analytics');
      
      const [
        jsasRes, 
        incidentsRes, 
        nearMissRes,
        inspectionStatsRes,
        auditsRes,
        divisionsRes,
        toolboxTalksRes,
        preConJhaRes,
        policyViolationsRes
      ] = await Promise.all([
        axiosInstance.get('/jsa').catch(() => ({ data: [] })),
        axiosInstance.get('/incident-reports').catch(() => ({ data: [] })),
        axiosInstance.get('/near-miss').catch(() => ({ data: [] })),
        axiosInstance.get('/inspections/stats/summary').catch(() => ({ data: { total: 0 } })),
        axiosInstance.get('/safety-audits').catch(() => ({ data: [] })),
        axiosInstance.get('/divisions').catch(() => ({ data: [] })),
        axiosInstance.get('/toolbox-talks').catch(() => ({ data: [] })),
        axiosInstance.get('/pre-construction-jha').catch(() => ({ data: [] })),
        axiosInstance.get('/policy-violations').catch(() => ({ data: [] })),
      ]);

      setStats({
        workers: analyticsRes.data.workers || analyticsRes.data.totalUsers || 0,
        courses: analyticsRes.data.totalCourses || 0,
        completions: analyticsRes.data.totalCompletions || 0,
        certificates: analyticsRes.data.totalCertificates || 0,
        jsas: jsasRes.data.length,
        incidents: incidentsRes.data.length,
        nearMiss: nearMissRes.data.length,
        inspections: inspectionStatsRes.data.total || 0,
        audits: Array.isArray(auditsRes.data) ? auditsRes.data.length : 0,
        divisions: Array.isArray(divisionsRes.data) ? divisionsRes.data.length : 0,
        toolboxTalks: Array.isArray(toolboxTalksRes.data) ? toolboxTalksRes.data.length : 0,
        preConJha: Array.isArray(preConJhaRes.data) ? preConJhaRes.data.length : 0,
        policyViolations: Array.isArray(policyViolationsRes.data)
          ? policyViolationsRes.data.length
          : (policyViolationsRes.data?.total || policyViolationsRes.data?.violations?.length || 0),
        company: analyticsRes.data.company,
        division: analyticsRes.data.division, // Division info for badge
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  // Check if company is in trial mode with limited features
  const isTrialLimited = stats?.company?.subscriptionStatus === 'trial' && 
                          stats?.company?.trialFeaturesOnly === true;
  
  // Calculate days remaining in trial
  const getTrialDaysRemaining = () => {
    if (!stats?.company?.trialEndDate) return 0;
    const endDate = new Date(stats.company.trialEndDate);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const navigationCards = [
    {
      title: t('home.userManagement'),
      description: t('home.userManagementDesc'),
      icon: Users,
      color: 'purple',
      path: '/admin/users',
      stat: stats?.workers || 0,
      statLabel: t('home.totalWorkers'),
      roles: ['company_admin', 'division_admin'],
      trialAccess: false
    },
    {
      title: t('home.divisionsTitle'),
      description: t('home.divisionsDesc'),
      icon: Building2,
      color: 'cyan',
      path: '/admin/divisions',
      stat: stats?.divisions ?? 0,
      statLabel: t('common.division'),
      roles: ['company_admin', 'division_admin'],
      trialAccess: false
    },
    {
      title: t('home.newHireTitle'),
      description: t('home.newHireDesc'),
      icon: UserPlus,
      color: 'green',
      path: '/admin/new-hires',
      stat: '-',
      statLabel: t('home.workerDocuments'),
      roles: ['company_admin', 'division_admin'],
      trialAccess: false
    },
    {
      title: t('home.trainingTitle'),
      description: t('home.trainingDesc'),
      icon: GraduationCap,
      color: 'indigo',
      path: '/admin/training',
      stat: stats?.courses || 0,
      statLabel: t('home.totalCourses'),
      roles: ['company_admin', 'division_admin'],
      trialAccess: false
    },
    {
      title: t('home.incidentTitle'),
      description: t('home.incidentDesc'),
      icon: AlertTriangle,
      color: 'red',
      path: '/admin/incidents',
      stat: stats?.incidents || 0,
      statLabel: t('home.totalIncidents'),
      trialAccess: true
    },
    {
      title: t('home.nearMissTitle'),
      description: t('home.nearMissDesc'),
      icon: AlertTriangle,
      color: 'yellow',
      path: '/admin/near-miss',
      stat: stats?.nearMiss || 0,
      statLabel: t('home.totalNearMiss'),
      trialAccess: false
    },
    {
      title: t('home.jsaTitle'),
      description: t('home.jsaDesc'),
      icon: FileText,
      color: 'blue',
      path: '/admin/jsas',
      stat: stats?.jsas || 0,
      statLabel: t('home.totalJSAs'),
      trialAccess: true
    },
    {
      title: t('home.jhaTitle'),
      description: t('home.jhaDesc'),
      icon: ClipboardList,
      color: 'indigo',
      path: '/admin/pre-construction-jha',
      stat: stats?.preConJha ?? 0,
      statLabel: 'Pre-Construction JHAs',
      trialAccess: true
    },
    {
      title: t('home.auditTitle'),
      description: t('home.auditDesc'),
      icon: Shield,
      color: 'green',
      path: '/admin/audits',
      stat: stats?.audits ?? 0,
      statLabel: 'Total Audits',
      trialAccess: true
    },
    {
      title: t('home.policyViolationsTitle'),
      description: t('home.policyViolationsDesc'),
      icon: ShieldAlert,
      color: 'red',
      path: '/admin/policy-violations',
      stat: stats?.policyViolations ?? 0,
      statLabel: t('home.policyViolationsStatLabel'),
      trialAccess: true,
      roles: ['company_admin', 'division_admin', 'admin']
    },
    {
      title: 'OSHA Reports',
      description: 'Auto-generate OSHA Form 300, 300A, and 301 from your incident records',
      icon: FileText,
      color: 'blue',
      path: '/admin/osha-reports',
      stat: '-',
      statLabel: 'Compliance',
      trialAccess: true,
      roles: ['company_admin', 'division_admin', 'admin']
    },
    {
      title: 'Pending Approvals',
      description: 'Review workers who signed up with your company code before granting full access',
      icon: UserCheck,
      color: 'amber',
      path: '/admin/pending-approvals',
      stat: pendingCount,
      statLabel: pendingCount === 1 ? 'Pending' : 'Pending',
      trialAccess: true,
      roles: ['company_admin', 'division_admin', 'admin']
    },
    {
      title: 'LOTO Points',
      description: 'AI-extracted equipment lockout/tagout procedures from your SOPs — 29 CFR 1910.147',
      icon: Zap,
      color: 'orange',
      path: '/admin/loto-points',
      stat: '-',
      statLabel: 'Equipment',
      trialAccess: true,
      roles: ['company_admin', 'division_admin', 'admin']
    },
    {
      title: 'Toolbox Talks',
      description: 'Sessions, PDFs, and attendance tracking',
      icon: Presentation,
      color: 'orange',
      path: '/admin/toolbox',
      stat: stats?.toolboxTalks ?? 0,
      statLabel: 'Talks & Sessions',
      trialAccess: false
    },
    {
      title: 'Custom PDF Forms',
      description: 'Create, upload & map custom PDF forms for workers',
      icon: ClipboardList,
      color: 'purple',
      path: '/admin/custom-forms',
      stat: '-',
      statLabel: 'Form Builder',
      trialAccess: false,
      roles: ['company_admin', 'division_admin']
    },
    {
      title: t('home.inspectionTemplatesTitle'),
      description: t('home.inspectionTemplatesDesc'),
      icon: ClipboardCheck,
      color: 'indigo',
      path: '/admin/inspection-templates',
      stat: '-',
      statLabel: 'Template Creator',
      trialAccess: false,
      roles: ['company_admin', 'division_admin', 'admin']
    },
    {
      title: t('home.inspectionsTitle'),
      description: t('home.inspectionsDesc'),
      icon: FileCheck,
      color: 'teal',
      path: '/admin/inspections',
      stat: stats?.inspections ?? '-',
      statLabel: t('home.totalInspections'),
      trialAccess: false,
      roles: ['company_admin', 'division_admin', 'admin']
    },
    {
      title: 'Messages',
      description: 'Send messages and training reminders',
      icon: MessageSquare,
      color: 'sky',
      path: '/admin/messages',
      stat: '-',
      statLabel: 'Communications',
      trialAccess: false
    },
    {
      title: 'Safety Analytics',
      description: 'JSA, Audits, Incidents & Injury Body Map',
      icon: PieChart,
      color: 'orange',
      path: '/safety-analytics-preview',
      stat: '-',
      statLabel: 'Safety Metrics',
      trialAccess: false
    },
    {
      title: 'Safety Dashboard',
      description: 'Leaderboard, Goals, Risk Prediction & Heat Map',
      icon: Trophy,
      color: 'yellow',
      path: '/safety-dashboard',
      stat: '-',
      statLabel: 'Advanced Analytics',
      trialAccess: false
    },
    {
      title: 'Policies & SDS Library',
      description: 'Upload policies, procedures & safety data sheets',
      icon: FileText,
      color: 'indigo',
      path: '/admin/policies',
      stat: '-',
      statLabel: 'Document Library',
      trialAccess: false
    },
    {
      title: t('home.companySettingsTitle'),
      description: t('home.companySettingsDesc'),
      icon: Settings,
      color: 'gray',
      path: '/admin/settings',
      stat: '-',
      statLabel: t('common.settings'),
      roles: ['company_admin', 'division_admin'],
      trialAccess: true
    },
    {
      title: 'GC / Subcontractors',
      description: 'Manage projects & subcontractor oversight',
      icon: Building2,
      color: 'indigo',
      path: '/gc/dashboard',
      stat: '-',
      statLabel: 'Projects',
      roles: ['company_admin', 'gc_admin'],
      requiresGcAccess: true,
      trialAccess: false
    },
    {
      title: 'My GC Projects',
      description: 'View invitations & projects from General Contractors',
      icon: Briefcase,
      color: 'teal',
      path: '/sub/my-projects',
      stat: '-',
      statLabel: 'GC Invitations',
      roles: ['company_admin', 'division_admin'],
      trialAccess: false
    },
  ].filter(card => {
    // Filter by role
    if (card.roles && !card.roles.includes(user?.role)) return false;
    // Filter GC tile by company setting
    if (card.requiresGcAccess) {
      const company = stats?.company;
      const hasAccess = company?.companyType === 'gc' || 
                        company?.gcTileUnlocked === true || 
                        user?.role === 'gc_admin';
      return hasAccess;
    }
    // Filter by trial access
    if (isTrialLimited && !card.trialAccess) {
      return false; // Hide features not available in trial
    }
    // Per-company hide override
    const tileKey = (card.path || '').replace(/^.*\//, '');
    if (tileOverrides[tileKey]?.hidden) return false;
    return true;
  }).map(card => {
    // Per-company name/description override
    const tileKey = (card.path || '').replace(/^.*\//, '');
    const ov = tileOverrides[tileKey];
    if (!ov) return { ...card, tileKey };
    const isEs = (i18n.language || 'en').toLowerCase().startsWith('es');
    const overrideTitle = isEs ? ov.nameEs : ov.nameEn;
    const overrideDesc = isEs ? ov.descEs : ov.descEn;
    return {
      ...card,
      tileKey,
      title: (overrideTitle && overrideTitle.trim()) || card.title,
      description: (overrideDesc && overrideDesc.trim()) || card.description,
    };
  });

  const getColorClasses = (color) => {
    const colors = {
      blue: 'from-blue-50 to-blue-100 border-blue-200 hover:border-blue-300',
      red: 'from-red-50 to-red-100 border-red-200 hover:border-red-300',
      yellow: 'from-yellow-50 to-yellow-100 border-yellow-200 hover:border-yellow-300',
      green: 'from-green-50 to-green-100 border-green-200 hover:border-green-300',
      purple: 'from-purple-50 to-purple-100 border-purple-200 hover:border-purple-300',
      indigo: 'from-indigo-50 to-indigo-100 border-indigo-200 hover:border-indigo-300',
      cyan: 'from-cyan-50 to-cyan-100 border-cyan-200 hover:border-cyan-300',
      orange: 'from-orange-50 to-orange-100 border-orange-200 hover:border-orange-300',
      pink: 'from-pink-50 to-pink-100 border-pink-200 hover:border-pink-300',
      teal: 'from-teal-50 to-teal-100 border-teal-200 hover:border-teal-300',
      sky: 'from-sky-50 to-sky-100 border-sky-200 hover:border-sky-300',
      gray: 'from-gray-50 to-gray-100 border-gray-200 hover:border-gray-300',
    };
    return colors[color] || colors.blue;
  };

  const getIconColorClasses = (color) => {
    const colors = {
      blue: 'text-blue-600',
      red: 'text-red-600',
      yellow: 'text-yellow-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      indigo: 'text-indigo-600',
      cyan: 'text-cyan-600',
      orange: 'text-orange-600',
      pink: 'text-pink-600',
      teal: 'text-teal-600',
      sky: 'text-sky-600',
      gray: 'text-gray-600',
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trial Banner - Only show for trial accounts on web (not native apps per App Store guidelines) */}
        {isTrialLimited && !isNativeApp && (
          <div className="mb-6 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-full">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">3-Day Trial - {getTrialDaysRemaining()} days remaining</h3>
                  <p className="text-orange-100 text-sm">
                    You have access to JSAs, Audits & Incident Reports. Contact your account manager to unlock all features.
                  </p>
                </div>
              </div>
              <Button 
                variant="secondary" 
                className="bg-white text-orange-600 hover:bg-orange-50"
                onClick={() => window.location.href = 'mailto:sales@safetymindai.com?subject=SafetyMind AI - Full Access Request'}
              >
                Contact Sales
              </Button>
            </div>
          </div>
        )}

        {/* SafetyMind AI Branding */}
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-xl">SM</span>
            </div>
            <div className="text-left">
              <h2 className="text-2xl font-bold">
                <span className="text-orange-500">Safety</span>
                <span className="text-gray-900">Mind</span>
                <span className="text-orange-500"> AI</span>
              </h2>
              <p className="text-sm text-gray-500 italic">Safety That Thinks</p>
            </div>
          </div>
        </div>

        {/* Welcome Header with Logout */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t('home.welcomeAdmin')}, {user?.name}!
            </h1>
            {stats?.division ? (
              <div className="flex items-center gap-2">
                <p className="text-gray-600">
                  Managing {stats.division.name}{user?.companyName ? ` · ${user.companyName}` : ''}
                </p>
                <span className="px-2 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded">
                  Division View
                </span>
              </div>
            ) : (
              <p className="text-gray-600">
                {user?.companyName || t('home.quickActions')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            {user?.role === 'company_admin' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/tile-settings')}
                className="gap-2"
                data-testid="admin-home-customize-tiles-btn"
              >
                <Settings className="w-4 h-4" />
                {i18n.language?.startsWith('es') ? 'Personalizar Tarjetas' : 'Customize Tiles'}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleLogout}
              className="gap-2"
              data-testid="admin-home-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {t('common.logout')}
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        {!loading && stats && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">{t('home.totalWorkers')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.workers}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">{t('home.totalCourses')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-indigo-600">{stats.courses}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">{t('home.totalCompletions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{stats.completions}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">{t('home.totalCertificates')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{stats.certificates}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Cards/Tiles */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {navigationCards.map((card) => (
            <Card
              key={card.path}
              className={`bg-gradient-to-br ${getColorClasses(card.color)} ${card.locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:shadow-lg'} transition-all border-2 relative`}
              onClick={() => !card.locked && navigate(card.path)}
            >
              {card.locked && (
                <div className="absolute top-3 right-3 bg-gray-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Coming Soon
                </div>
              )}
              {card.comingSoon && !card.locked && (
                <div className="absolute top-3 right-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  Coming Soon
                </div>
              )}
              <CardHeader>
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg bg-white flex items-center justify-center shadow-sm ${card.locked ? 'opacity-50' : ''}`}>
                    <card.icon className={`w-6 h-6 ${getIconColorClasses(card.color)}`} />
                  </div>
                  {!card.comingSoon && !card.locked && <ArrowRight className="w-5 h-5 text-gray-400" />}
                </div>
                <CardTitle className={`text-xl mb-2 ${card.locked ? 'text-gray-500' : ''}`}>{card.title}</CardTitle>
                <CardDescription className={`text-sm ${card.locked ? 'text-gray-400' : 'text-gray-700'}`}>
                  {card.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <div>
                    <div className={`text-2xl font-bold ${card.locked ? 'text-gray-400' : 'text-gray-900'}`}>{card.stat}</div>
                    <div className={`text-xs mt-0.5 ${card.locked ? 'text-gray-400' : 'text-gray-600'}`}>{card.statLabel}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default AdminHome;
